// paex-cart-bridge.js — shared cart-bridge toolkit (M5 dedupe, 2026-08-03,
// CUSTOM-CODE-REVIEW A6). ONE home for the primitives that were re-implemented
// across paex-attach.js, paex-cart.js, paex-quick-add.js and
// paex-delivery-method.js (three of which co-load on a PDP): the money()
// format ladder, the product-form sections descriptor list, the drawer focus
// re-trap, and the cartUpdate publish with its typeof-gates.
//
// M6 (2026-08-27, cart-drawer fix batch): adds the cart mutation QUEUE and the
// token-gated section painter — see the block below the M5 primitives.
//
// Load contract: every consumer's Liquid emitter renders this tag immediately
// before its own script tag, all deferred, so defer order guarantees the
// bridge executes first (multi-emitter + idempotence guard — the same
// re-execution-safe pattern as paex-store-core.js; the browser fetches one
// cached copy). Consumers still look the bridge up at call time and degrade
// to a no-op/last-resort exactly like their old typeof-gates did. Native
// cart.js is the one script that executes BEFORE the bridge on the drawer
// section (paex-cart-drawer.liquid emits it first), so its integration is a
// call-time lookup with a direct-execution fallback.
//
// typeof-gate lore (W3 audit 2026-08-01), preserved verbatim: global.js
// declares webvista / PUB_SUB_EVENTS as top-level consts — script-scope
// globals that are NOT window properties — so `window.webvista && …` is
// permanently false on the storefront. Feature-detect via typeof on the bare
// identifier (safe when global.js failed to load). SectionDynamicUpdate is a
// top-level class, never window.*.

(function () {
  'use strict';
  if (window.PaexCartBridge) return;

  function routeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  // Shop money_format ladder: webvista.formatPriceAmount (matches every other
  // price on the page), then Intl, then a bare last-resort string.
  function money(cents) {
    try {
      var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
      if (typeof webvista !== 'undefined' && webvista && typeof webvista.formatPriceAmount === 'function') {
        var native = webvista.formatPriceAmount(cents, currency);
        if (native) return native; // shop money_format — "$30.01"
      }
      var locale = ((window.Shopify && Shopify.locale) || 'en') + '-CA';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function publishCart(source, cartData) {
    if (typeof webvista !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && typeof webvista.publish === 'function') {
      webvista.publish(PUB_SUB_EVENTS.cartUpdate, { source: source, cartData: cartData });
    }
  }

  // Same descriptor shapes as product-form.getSectionsToRender(). The caller
  // decides WHICH drawer element qualifies (silence gate, .paex-cart check —
  // per-consumer semantics stay in the consumer); pass null to get the
  // icon-bubble entry only.
  function sectionsToRender(drawer) {
    var sections = [
      { id: 'Cart-Icon-Bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
    if (drawer && drawer.dataset && drawer.dataset.section) {
      sections.push({ id: 'Cart-Drawer', section: drawer.dataset.section, selector: '#Cart-Drawer-Details' });
    }
    return sections;
  }

  // Swapping #Cart-Drawer-Details while the drawer is OPEN detaches the focus
  // trap's cached edge nodes — re-arm it, exactly like native cart.js does
  // after its own section patches. [data-trap] lives INSIDE #Cart-Drawer in
  // both drawer variants (paex-cart-drawer.liquid:72, cart-drawer.liquid:27),
  // so querySelector-then-self covers every real DOM shape.
  function retrapDrawer(drawer) {
    drawer = drawer || document.getElementById('Cart-Drawer');
    if (!drawer || !drawer.hasAttribute('open')) return;
    if (!(typeof webvista !== 'undefined' && webvista && typeof webvista.trapFocus === 'function')) return;
    var trap = drawer.querySelector('[data-trap]') || drawer;
    var active = document.activeElement;
    try {
      webvista.trapFocus(trap, trap.contains(active) ? active : null);
    } catch (error) { /* containment re-arms on the next repaint */ }
  }

  /* ------------------------------------------------------------------ *
   * M6 (2026-08-27): cart mutation queue + render-version guard.
   *
   * Shopify serializes cart WRITES server-side; the client failures this
   * layer removes are (a) response ORDER — an older section snapshot
   * painting over a newer one ("phantom items"), and (b) positional-index
   * staleness between overlapping /cart/change calls (wrong line removed).
   * enqueue() runs every client-initiated cart mutation/refresh alone
   * (FIFO); applySections() drops any paint older than the newest landed.
   *
   * RULE: a queued task may CALL enqueue() (fire-and-forget) but must never
   * AWAIT a nested enqueue() — it would deadlock behind itself. The 20s
   * timebox advances the queue past a hung fetch (the hung task's own
   * promise still settles for its caller whenever the network gives up).
   * ------------------------------------------------------------------ */
  var cartQueue = { tail: Promise.resolve(), seq: 0, painted: 0 };

  function enqueue(task) {
    var release;
    var gate = new Promise(function (resolve) { release = resolve; });
    var result = gate.then(task);
    cartQueue.tail = cartQueue.tail.then(function () {
      release();
      var settled = result.then(function () {}, function () {});
      var timebox = new Promise(function (resolve) { setTimeout(resolve, 20000); });
      return Promise.race([settled, timebox]);
    });
    return result;
  }

  function nextToken() {
    cartQueue.seq += 1;
    return cartQueue.seq;
  }

  // Token-gated SectionDynamicUpdate wrapper. Skips section payloads that are
  // not strings (global.js getSectionInnerHTML would otherwise parse
  // "undefined" into the drawer) and drops any response older than the newest
  // paint. Returns true when it painted.
  function applySections(sections, responseSections, token) {
    if (typeof SectionDynamicUpdate === 'undefined' || !responseSections) return false;
    if (typeof token === 'number') {
      if (token < cartQueue.painted) return false; // an older response landed late — drop it
      cartQueue.painted = token;
    }
    var valid = (sections || []).filter(function (entry) {
      return entry && entry.section && typeof responseSections[entry.section] === 'string';
    });
    if (!valid.length) return false;
    SectionDynamicUpdate.updateSections(valid, responseSections);
    retrapDrawer(null);
    return true;
  }

  function defaultDrawer() {
    var el = document.getElementById('Cart-Drawer');
    if (!el || el.hasAttribute('data-status-silence')) return null;
    return el;
  }

  // Loading veil over #Cart-Drawer-Details while a refresh is in flight, so a
  // stale (often EMPTY) pre-mutation paint never reads as final. Styles live
  // in paex-cart.css under [data-paex-cart-loading]. A11y (review 2026-08-27):
  // `inert` + a trap re-arm close the forward-Tab leak (hidden edge nodes let
  // Tab walk out of the aria-modal dialog), and a status announcer OUTSIDE
  // the veiled region says why the content vanished.
  function setDrawerLoading(on) {
    var el = document.getElementById('Cart-Drawer');
    if (!el) return;
    el.toggleAttribute('data-paex-cart-loading', !!on);
    var details = document.getElementById('Cart-Drawer-Details');
    if (details) {
      details.setAttribute('aria-busy', on ? 'true' : 'false');
      try { details.inert = !!on; } catch (error) { /* pre-inert browsers: veil stays visual-only */ }
    }
    var status = el.querySelector('[data-paex-cart-loading-status]');
    if (status) status.textContent = on ? (status.getAttribute('data-loading-text') || '') : '';
    retrapDrawer(el); // edge nodes changed (details inert) — re-resolve while open
  }

  // One-stop cart POST: sections ride the request body (single round trip —
  // the native product-form contract), the paint is token-gated, the publish
  // fires after the paint. Deliberately toast-free: error UX stays with each
  // caller (paex-cart.js toasts, paex-quick-add.js renders an inline row).
  // Resolves to Shopify's JSON — the error shape {status,message,description}
  // included — or null when the response body was unreadable.
  function mutate(path, payload, opts) {
    opts = opts || {};
    // BOPIS (lane B, 2026-10-22): every theme-side add goes through the
    // fulfilment pre-check (seams §1 step 3). paexFulfil.gate resolves the
    // cart's service, checks the stock for it and either writes the mode on
    // a first add or dispatches paex:fulfil-intercept. Blocked → resolves
    // null (no POST) and lifts the instant-open veil; a gate exception never
    // blocks the add (fail open = today's behaviour). opts.skipFulfil
    // bypasses it (an add the UI already re-checked).
    if (path === 'cart/add.js' && !opts.skipFulfil && window.paexFulfil && typeof paexFulfil.gate === 'function') {
      return Promise.resolve()
        .then(function () { return paexFulfil.gate(payload, opts); })
        .then(function (verdict) {
          if (verdict && verdict.ok === false) {
            // veil off; the drawer closes too unless the interception UI claimed the event
            if (window.PaexCartInstant && typeof PaexCartInstant.lift === 'function') PaexCartInstant.lift(!verdict.claimed);
            return null;
          }
          return mutateNow(path, payload, opts);
        }, function () { return mutateNow(path, payload, opts); });
    }
    return mutateNow(path, payload, opts);
  }

  function mutateNow(path, payload, opts) {
    opts = opts || {};
    var sections = opts.sections || sectionsToRender('drawer' in opts ? opts.drawer : defaultDrawer());
    return enqueue(function () {
      var token = nextToken();
      var body = {};
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) body[k] = payload[k];
      }
      body.sections = sections.map(function (s) { return s.section; }).join(',');
      body.sections_url = window.location.pathname;
      // keepalive only where an add must survive a navigation (paex-attach
      // passes opts.keepalive): Chrome rejects keepalive requests outright
      // when its in-flight keepalive budget (shared with analytics beacons)
      // is full, which left drawer adds hanging (review 2026-10-10). A 15 s
      // abort makes a stalled request settle so callers can recover.
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var abortTimer = controller ? setTimeout(function () { controller.abort(); }, 15000) : null;
      return fetch(routeRoot() + path, {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        keepalive: Boolean(opts.keepalive),
        signal: controller ? controller.signal : undefined,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) { clearTimeout(abortTimer); return response.json().catch(function () { return null; }); }, function (error) { clearTimeout(abortTimer); throw error; })
        .then(function (resp) {
          if (resp && resp.sections) applySections(sections, resp.sections, token);
          if (resp && !resp.status && opts.source) publishCart(opts.source, resp);
          return resp;
        });
    });
  }

  // Cache-proof drawer/bubble refresh for mutations the theme did NOT perform
  // itself (RCC app adds, Discount Kit gift writes). The naive form of this
  // GET is what shipped the "empty drawer" bug: same-URL, heuristically
  // cacheable (Shopify sends no Cache-Control on ?sections= — verified
  // 2026-08-27), silent on !ok and on throw. This one: no-store + buster,
  // attempt → retry → /cart.js probe (+ final attempt when the drawer paints
  // empty but the cart has items) → error toast. Never silent, and the veil
  // always clears.
  function refreshSections(opts) {
    opts = opts || {};
    var drawerEl = 'drawer' in opts ? opts.drawer : defaultDrawer();
    var sections = opts.sections || sectionsToRender(drawerEl);
    if (opts.skeleton) setDrawerLoading(true);
    return enqueue(function () {
      var token = nextToken();
      var names = sections.map(function (s) { return s.section; }).join(',');
      var attempt = function () {
        var url = window.location.pathname + '?sections=' + encodeURIComponent(names) + '&_=' + Date.now();
        return fetch(url, { cache: 'no-store', credentials: 'same-origin' }).then(function (response) {
          if (!response.ok) throw new Error('sections HTTP ' + response.status);
          return response.json();
        });
      };
      return attempt()
        .catch(function () { return attempt(); })
        .catch(function () {
          return fetch(routeRoot() + 'cart.js', { cache: 'no-store', credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cart) {
              var showsEmpty = drawerEl && drawerEl.querySelector('.paex-cart__empty');
              if (cart && cart.item_count > 0 && showsEmpty) return attempt();
              throw new Error('cart sections refresh failed');
            });
        })
        .then(function (payload) {
          applySections(sections, payload, token);
          if (opts.source) publishCart(opts.source, opts.cartData || null);
          return payload;
        })
        .catch(function () {
          try {
            if (typeof webvista !== 'undefined' && typeof webvista.popToast === 'function'
              && window.cartStrings && window.cartStrings.error) {
              webvista.popToast(window.cartStrings.error, 'error'); // native string — no new locale keys
            }
          } catch (error) { /* toast is best-effort */ }
          return null;
        })
        .finally(function () {
          if (opts.skeleton) setDrawerLoading(false);
        });
    });
  }

  /* ------------------------------------------------------------------ *
   * Discount Kit repaint hook. The app embed runs with
   * refresh_on_cart_change=false, so the THEME owns the repaint after gift
   * writes — the app docs are explicit: "you MUST manually update/refresh
   * the cart UI". Verified live 2026-08-27: the app fires
   * discount_kit:cart_changed after each gift mutation and
   * discount_kit:gift_adjustments around it (including an EMPTY
   * {add:[],remove:{}} on plain page loads — hence the has-work guard, so
   * page loads cost zero extra requests). Multiple events per adjustment
   * trail-debounce into ONE queued, token-gated refresh.
   * ------------------------------------------------------------------ */
  var dkTimer = null;
  function dkRefresh() {
    clearTimeout(dkTimer);
    dkTimer = setTimeout(function () {
      refreshSections({ source: 'discount-kit' });
    }, 150);
  }
  document.addEventListener('discount_kit:cart_changed', function () {
    dkRefresh();
  });
  document.addEventListener('discount_kit:gift_adjustments', function (event) {
    var detail = event && event.detail;
    if (!detail) return;
    var add = detail.add;
    var hasAdd = !!add && (Array.isArray(add)
      ? add.length > 0
      : !!(add.options && add.options.length));
    var hasRemove = !!detail.remove && Object.keys(detail.remove).length > 0;
    if (hasAdd || hasRemove) dkRefresh();
  });

  // BOPIS (lane B, seams §3.1): the cart-attribute helper. Sections ride the
  // body (drawer repaints, token-gated), cartUpdate is published with
  // source 'paex-fulfil' and the full cart JSON (cart/update.js returns the
  // cart). Clear = { paex_fulfil: '' }. Pass { drawer: null } to write without
  // a details repaint (edd_postal while typing — the reason paex-cart.js
  // cartPost sends no sections).
  function setAttributes(attrs, opts) {
    return mutate('cart/update.js', { attributes: attrs }, Object.assign({ source: 'paex-fulfil' }, opts || {}));
  }

  window.PaexCartBridge = {
    routeRoot: routeRoot,
    money: money,
    publishCart: publishCart,
    sectionsToRender: sectionsToRender,
    retrapDrawer: retrapDrawer,
    enqueue: enqueue,
    nextToken: nextToken,
    applySections: applySections,
    setDrawerLoading: setDrawerLoading,
    mutate: mutate,
    setAttributes: setAttributes,
    refreshSections: refreshSections,
  };
})();
